/**
 * Cash, lives, and the two transitions that end a run.
 *
 * ## Where cash comes from
 *
 * Three sources, in descending order of how much they matter:
 *
 * 1. **Popping.** `Projectiles.resolve` already pays out inline:
 *    `hpRemoved × ECONOMY.cashPerHp × archetype.bounty × pylonBonus`.
 *    {@link cashForDamage} is the same formula in one place so the balance
 *    tooling and the HUD projection cannot drift from the sim. Note what it
 *    does *not* count: shield absorption. Damage sunk into an overshield is
 *    scored for the tower's damage counter and pays nothing. That is the
 *    intended pressure from the `shielded` family — they cost you money to
 *    kill, not just time.
 * 2. **End of round.** `roundBonusBase + roundBonusPerRound × round`. A flat
 *    100 plus one per round is a rounding error by round 40, which is correct:
 *    it exists to keep the opening ten rounds solvent, not to fund the endgame.
 *    Through rounds 1–6 it is most of the income; by round 90 it is under a
 *    thousandth of it.
 * 3. **Early start.** `earlyStartBonusPerSecond` per unused prep second. Two
 *    cash a second against a six second prep is 12 cash — trivially small on
 *    purpose. Starting early should be a *tempo* decision, not an income
 *    strategy, or the optimal play becomes rushing every round and the pacing
 *    work in `Waves.ts` is wasted.
 *
 * ## Where cash goes
 *
 * `spend` and `refund` are the only two functions that move `world.cash` for a
 * purchase. Both are guarded and both return whether they did anything, so a
 * caller cannot half-apply a transaction and leave the wallet negative.
 *
 * ## Lives
 *
 * `entities/Enemy.ts` deducts `archetype.leak` directly when a body reaches the
 * exit. {@link registerLeak} exists so the ledger sees the same event; it does
 * not double-deduct. Defeat and victory are decided in {@link checkTransitions},
 * which every system that can change lives should be stepped in front of.
 */

import { ECONOMY, WAVES } from '@/core/config';
import type { EnemyArchetype, World } from '@/core/types';

// ────────────────────────────────────────────────────────────── earnings ──

/**
 * Cash paid per point of hit points removed, by round.
 *
 * **This curve is the single most important number in the economy** and it
 * exists because a flat rate cannot work. Enemy hit points grow about 100,000x
 * across a hundred rounds. Tower prices grow about 130x — the whole catalogue
 * maxed across twenty-four slots is `Balance.MAX_BOARD_COST`, roughly 2.57
 * million, and that ceiling is fixed by `towers/Catalog.ts`, not by anything
 * here. Pay a flat one cash per hit point and the player finishes round 100
 * holding tens of millions against a board that cost a fraction of it.
 *
 * That is not hypothetical. It is what a real playtest produced: **$1,187,679
 * banked at round 45, every tower maxed, nothing left to buy**, because
 * `towers/Projectiles.resolve` was paying `removed × cashPerHp × bounty` and
 * never multiplying by this curve. Every decision after about round 30 was
 * automatic. `resolve` now applies it; without that line none of the rest of
 * this file matters.
 *
 * So the rate decays hard: 0.17 cash per hit point through the opening decade,
 * 0.051 by round 20, 0.0075 by round 40, flattening near 0.008 afterwards. The
 * decay is steepest exactly where enemy hit points accelerate fastest (rounds
 * 10–40) and flat afterwards, so late rounds still pay proportionally more for
 * killing more.
 *
 * Anchors are log-interpolated, and they were **solved** rather than guessed:
 * `Balance` binary-searches the cash budget at which each round's best board
 * reaches the target coverage, and these anchors are a smooth monotone fit
 * through that curve. The fit is what makes the two claims in
 * `tests/economy.test.ts` hold at once — total income across a hundred rounds
 * stays under the cost of a finished board (so the tree is never exhausted),
 * and stays above a third of it (so the last thirty rounds still have
 * purchases in them).
 */
export const CASH_RATE_ANCHORS: readonly (readonly [number, number])[] = [
  [1, 0.2300],
  [10, 0.2200],
  [20, 0.1120],
  [30, 0.0640],
  [40, 0.0520],
  [50, 0.0228],
  [60, 0.0128],
  [70, 0.0077],
  [80, 0.0033],
  [90, 0.00135],
  [100, 0.0011],
];

/**
 * The round-dependent multiplier on `ECONOMY.cashPerHp`.
 *
 * Applied in three places and they must not drift: `towers/Projectiles.resolve`
 * (the live payout), {@link cashForDamage} (the shared formula) and
 * `Waves.wavePayout` (the projection `Balance` runs on).
 */
export function payoutScale(round: number): number {
  const r = round < 1 ? 1 : round > WAVES.count ? WAVES.count : round;
  for (let i = 1; i < CASH_RATE_ANCHORS.length; i++) {
    const [r1, v1] = CASH_RATE_ANCHORS[i];
    if (r > r1) continue;
    const [r0, v0] = CASH_RATE_ANCHORS[i - 1];
    const t = (r - r0) / (r1 - r0);
    return Math.exp(Math.log(v0) + t * (Math.log(v1) - Math.log(v0)));
  }
  return CASH_RATE_ANCHORS[CASH_RATE_ANCHORS.length - 1][1];
}

/**
 * Cash for hit points removed. Mirrors `Projectiles.resolve`, with the round
 * deflator applied.
 *
 * `bonus` is the multiplicative Pylon cash aura (`aura:cash:0.33` → 1.33),
 * already resolved by the tower system.
 */
export function cashForDamage(hpRemoved: number, bounty: number, bonus = 1, round = 1): number {
  if (hpRemoved <= 0) return 0;
  return hpRemoved * ECONOMY.cashPerHp * payoutScale(round) * bounty * bonus;
}

/** Full-kill payout for an enemy at its spawned hit points. Shields excluded. */
export function cashForKill(maxHp: number, archetype: EnemyArchetype, bonus = 1, round = 1): number {
  return cashForDamage(maxHp, archetype.bounty, bonus, round);
}

/** End-of-round bonus. Linear in the round number, deliberately small late. */
export function roundBonus(round: number): number {
  return ECONOMY.roundBonusBase + ECONOMY.roundBonusPerRound * round;
}

/** Bonus for skipping `seconds` of prep. */
export function earlyStartBonus(seconds: number): number {
  return seconds <= 0 ? 0 : Math.round(seconds * ECONOMY.earlyStartBonusPerSecond);
}

// ──────────────────────────────────────────────────────────────── ledger ──

/**
 * Run totals. Kept beside the world rather than inside it because
 * `core/types.ts` is owned by main and this is telemetry, not simulation
 * state — nothing in the sim reads it back.
 */
export interface Ledger {
  earnedPops: number;
  earnedBonuses: number;
  spent: number;
  refunded: number;
  leaks: number;
  livesLost: number;
  roundsCleared: number;
}

export function createLedger(): Ledger {
  return {
    earnedPops: 0, earnedBonuses: 0, spent: 0,
    refunded: 0, leaks: 0, livesLost: 0, roundsCleared: 0,
  };
}

let ledger: Ledger = createLedger();

export function economyLedger(): Ledger { return ledger; }
export function resetLedger(): Ledger { ledger = createLedger(); return ledger; }

// ──────────────────────────────────────────────────────────── the wallet ──

export function canAfford(world: World, cost: number): boolean {
  return Number.isFinite(cost) && cost >= 0 && world.cash >= cost;
}

/**
 * Deducts `cost` if the player has it. Returns false and changes nothing
 * otherwise — callers can treat a false return as "the purchase did not
 * happen" without needing to unwind anything.
 */
export function spend(world: World, cost: number): boolean {
  if (!canAfford(world, cost)) return false;
  world.cash -= cost;
  ledger.spent += cost;
  return true;
}

/** Returns cash to the player. Used for sells and for cancelled placements. */
export function refund(world: World, amount: number): number {
  if (!(amount > 0)) return 0;
  const given = Math.floor(amount);
  world.cash += given;
  ledger.refunded += given;
  return given;
}

/** Sell price of a tower with `invested` cash sunk into it. Floors, never rounds up. */
export function sellValue(invested: number): number {
  return Math.floor(invested * ECONOMY.sellRatio);
}

/** Records a pop payout that the projectile system already credited. */
export function notePop(amount: number): void {
  if (amount > 0) ledger.earnedPops += amount;
}

export function awardRoundBonus(world: World, round: number): number {
  const amount = roundBonus(round);
  world.cash += amount;
  ledger.earnedBonuses += amount;
  ledger.roundsCleared++;
  return amount;
}

export function awardEarlyStart(world: World, seconds: number): number {
  const amount = earlyStartBonus(seconds);
  if (amount <= 0) return 0;
  world.cash += amount;
  ledger.earnedBonuses += amount;
  return amount;
}

// ───────────────────────────────────────────────────────── lives + phase ──

/**
 * Books a leak in the ledger. **Does not** deduct lives — `Enemy.leakEnemy`
 * already did that, and two subtractions for one event is the kind of bug that
 * only shows up as "the run ended early sometimes".
 */
export function registerLeak(archetype: EnemyArchetype): void {
  ledger.leaks++;
  ledger.livesLost += archetype.leak;
}

/**
 * Defeat and victory. Idempotent, and safe to call every step from anywhere.
 *
 * Victory is claimed by `WaveDirector.complete` rather than here, because
 * "cleared round 100" is a wave fact; this function only ever ends a run
 * downward.
 */
export function checkTransitions(world: World): void {
  if (world.phase === 'defeat' || world.phase === 'victory') return;
  if (world.lives <= 0) {
    world.lives = 0;
    world.phase = 'defeat';
  }
}

/** Resets wallet, lives and ledger. Called on new run, not on round change. */
export function resetEconomy(world: World): void {
  world.cash = ECONOMY.startingCash;
  world.lives = ECONOMY.startingLives;
  resetLedger();
}

// ─────────────────────────────────────────────────────────── income model ──

/**
 * Projected cash after clearing every round up to and including `round`,
 * spending nothing.
 *
 * `payoutOf` is injected rather than imported so this stays a pure function of
 * the wave list — `Balance.ts` passes `wavePayout`, and a test can pass a
 * constant to check the accumulation itself.
 *
 * `efficiency` discounts the projection for the cash a real player loses to
 * leaks, overkill on splitters that die off-screen, and rounds where a body
 * escapes with half its health. 0.85 is a guess, stated as one in
 * docs/BALANCE.md.
 */
export function projectedCash(
  round: number,
  payoutOf: (round: number) => number,
  efficiency = 0.85,
): number {
  let cash = ECONOMY.startingCash;
  const top = Math.min(round, WAVES.count);
  for (let r = 1; r <= top; r++) {
    cash += payoutOf(r) * efficiency;
    cash += roundBonus(r);
  }
  return cash;
}

/**
 * Cash available *entering* `round` — that is, after clearing `round - 1`.
 * This is the budget the solvability check hands to a loadout.
 */
export function cashEntering(
  round: number,
  payoutOf: (round: number) => number,
  efficiency = 0.85,
): number {
  return projectedCash(round - 1, payoutOf, efficiency);
}
